import * as XLSX from "xlsx";
import { neutralizarFormula } from "@bookdental/schemas";

export type FormatoExport = "xlsx" | "csv";

export type ColunaExport<T> = {
  /** Cabeçalho no arquivo. É também o nome que a importação procura — mudar aqui quebra o retorno. */
  titulo: string;
  valor: (linha: T) => string | number | null | undefined;
  /** Campo do servidor que a coluna lê, onde a exportação pede só o que foi marcado (pacientes). */
  campo?: string;
};

/**
 * Exporta uma tabela para Excel ou CSV — o mesmo arquivo serve pra ler e pra reimportar.
 *
 * O arquivo carrega rótulos humanos ("Nome", "Telefone") e, com `incluirId`, uma coluna ID na frente.
 * Com ID preenchido, a importação ATUALIZA aquele registro; sem ID, cria um novo — é o que permite
 * exportar, mexer no Excel e devolver sem duplicar tudo. Fora das telas que importam, o ID é ruído.
 *
 * O que sai é o que está na tela: quem chama passa as linhas já filtradas e ordenadas. Exportar a
 * consulta inteira quando a pessoa filtrou seria devolver uma planilha diferente da que ela olha.
 */
export function exportarTabela<T>({
  nome,
  colunas,
  linhas,
  idDe,
  formato,
  incluirId,
}: {
  /** Sem extensão: vira "pacientes-2026-09-02.xlsx". */
  nome: string;
  colunas: ColunaExport<T>[];
  linhas: T[];
  idDe: (linha: T) => string;
  formato: FormatoExport;
  incluirId: boolean;
}): { arquivo: string; total: number } {
  const cabecalhos = [...(incluirId ? ["ID"] : []), ...colunas.map((c) => c.titulo)];
  const matriz = [
    cabecalhos,
    ...linhas.map((l) => [...(incluirId ? [idDe(l)] : []), ...colunas.map((c) => normalizar(c.valor(l)))]),
  ];

  // No CSV, texto que começa como fórmula ganha apóstrofo (mesma regra da exportação da organização);
  // no .xlsx a célula já vai como texto.
  const sheet = XLSX.utils.aoa_to_sheet(
    formato === "csv" ? matriz.map((l) => l.map((v) => (typeof v === "string" ? neutralizarFormula(v) : v))) : matriz,
  );
  sheet["!cols"] = cabecalhos.map((h, i) => ({
    wch: Math.min(46, Math.max(h.length + 2, ...matriz.slice(1, 200).map((l) => String(l[i] ?? "").length + 2))),
  }));

  const arquivo = `${nome}-${new Date().toISOString().slice(0, 10)}.${formato}`;

  if (formato === "csv") {
    // Ponto-e-vírgula e BOM porque o destino real é o Excel em português: com vírgula ele joga a
    // linha inteira numa célula só, e sem BOM "Adriana Gonçalves" chega como "AdrianaÂ GonÃ§alves".
    const csv = XLSX.utils.sheet_to_csv(sheet, { FS: ";" });
    baixar(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }), arquivo);
  } else {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Dados");
    XLSX.writeFile(wb, arquivo);
  }

  return { arquivo, total: linhas.length };
}

/** Célula vazia em vez de "null"/"undefined" escrito por extenso. */
function normalizar(v: string | number | null | undefined): string | number {
  return v == null ? "" : v;
}

function baixar(blob: Blob, nomeDoArquivo: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeDoArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Data pra célula: dd/mm/aaaa é o que a pessoa espera ver, e o importador aceita de volta. */
export function dataParaCelula(d: Date | string | null | undefined): string {
  if (!d) return "";
  const data = new Date(d);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleDateString("pt-BR");
}

/** Dia de calendário (nascimento, vencimento): guardado à meia-noite UTC ou como "aaaa-mm-dd".
 *  Passar pelo fuso local jogaria pro dia anterior no Brasil. */
export function diaParaCelula(d: Date | string | null | undefined): string {
  if (!d) return "";
  const iso = typeof d === "string" ? d : Number.isNaN(d.getTime()) ? "" : d.toISOString();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/** Dinheiro sai como NÚMERO em reais, não como "R$ 1.250,00": string com símbolo e separador vira
 *  texto no Excel — não soma, não ordena, e não volta pela importação. */
export function centavosParaCelula(cents: number | null | undefined): number | "" {
  return cents == null ? "" : Number((cents / 100).toFixed(2));
}
